import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Leaf, Truck, BadgeCheck, Star, Package, HeartHandshake, Users } from "lucide-react";
import { api } from "../lib/api";
import { maskLine, fadeUp, stagger, viewport } from "../lib/motion";
import { useQuote } from "../components/site/Header";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import Marquee from "../components/site/Marquee";
import ProductCard from "../components/site/ProductCard";
import QuoteForm from "../components/site/QuoteForm";

const HERO_IMG = "https://customer-assets-jai6qajn.emergentagent.net/job_business-food-pack/artifacts/ec3hjm74_ChatGPT%20Image%20Jul%2022%2C%202026%2C%2006_36_13%20PM.png";
const BRANDING_IMG = "https://customer-assets-jai6qajn.emergentagent.net/job_business-food-pack/artifacts/zuk7xugx_Custom%20Branding.png";

const HERO_LINES = ["Packaging that", "protects, presents", "& powers your brand"];

const STATS = [
  { n: "200+", l: "Product lines" },
  { n: "5000+", l: "Businesses served" },
  { n: "Pan-India", l: "Bulk delivery" },
  { n: "100%", l: "Food-grade" },
];

const WHY = [
  { icon: BadgeCheck, t: "Uncompromising quality", d: "Every product is food-grade and quality-checked before dispatch." },
  { icon: Package, t: "200+ product lines", d: "One partner for paper, sugarcane, plastic, aluminium & cutlery." },
  { icon: Truck, t: "Reliable bulk supply", d: "Deep inventory and on-time delivery across Tamil Nadu & India." },
  { icon: Leaf, t: "Sustainable range", d: "Compostable bagasse, paper and wooden alternatives in stock." },
  { icon: HeartHandshake, t: "Custom branding", d: "Turn packaging into marketing with custom-printed designs." },
  { icon: Users, t: "Dedicated support", d: "Helpful account managers who understand foodservice." },
];

const PROCESS = [
  { t: "Request a quote", d: "Tell us your products, quantities and city. We respond fast with bulk pricing." },
  { t: "Confirm & sample", d: "Approve pricing and, for custom orders, review print-ready samples." },
  { t: "Production", d: "We produce and quality-check your order in food-grade materials." },
  { t: "Delivery", d: "Reliable dispatch to your door across Tamil Nadu & India." },
];

export default function Home() {
  const openQuote = useQuote();
  const [featured, setFeatured] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  useEffect(() => {
    const order = ["Juice Pet Bottles", "Sipper Glasses", "Disposable PP Food Containers", "Sugarcane Plates and Bowls", "Paper Meal Box / Containers", "Paper Bags"];
    api.products().then((all) =>
      setFeatured(
        all
          .filter((p) => p.featured)
          .sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name))
          .slice(0, 6)
      )
    );
    api.testimonials().then(setTestimonials);
    api.industries().then(setIndustries);
    api.categories().then(setCategories);
    api.faqs().then((f) => setFaqs(f.slice(0, 6)));
    api.blog().then((b) => setBlogs(b.slice(0, 3)));
  }, []);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[92vh] flex items-center bg-[#F8FAF8] overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,#4CAF5015,transparent_45%)]" />
        <div className="max-w-7xl mx-auto px-5 md:px-10 w-full grid lg:grid-cols-12 gap-10 items-center py-16 relative z-10">
          <div className="lg:col-span-7">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
              className="inline-flex items-center gap-2 bg-white border border-[#E2E8E4] rounded-full px-4 py-1.5 mb-7"
            >
              <span className="w-2 h-2 rounded-full bg-[#4CAF50] animate-pulse" />
              <span className="text-xs font-bold tracking-[0.15em] uppercase text-[#1D6B2A]">
                Chennai&apos;s B2B Packaging Partner
              </span>
            </motion.div>

            <h1 className="font-head text-[2.7rem] sm:text-6xl lg:text-7xl font-medium tracking-tighter leading-[1.03] text-[#0A1C0E]">
              {HERO_LINES.map((line, i) => (
                <span key={i} className="reveal-mask">
                  <motion.span
                    className="block"
                    variants={maskLine}
                    custom={i}
                    initial="hidden"
                    animate="show"
                  >
                    {i === 2 ? (
                      <>
                        &amp; powers your <span className="text-[#1D6B2A]">brand</span>
                      </>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              variants={fadeUp}
              custom={1}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
              className="text-lg text-[#3A4D3E] mt-7 max-w-xl leading-relaxed"
            >
              Premium food packaging for restaurants, cloud kitchens, hotels, caterers and
              wholesalers. Paper, sugarcane, aluminium & more — delivered at scale.
            </motion.p>

            <motion.div
              variants={fadeUp}
              custom={2}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
              className="mt-9 flex flex-wrap gap-4"
            >
              <button
                onClick={() => openQuote()}
                data-testid="hero-quote-btn"
                className="group bg-[#1D6B2A] text-white rounded-full pl-8 pr-6 py-4 font-semibold hover:bg-[#154d1e] transition-colors flex items-center gap-2"
              >
                Request a Quote
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <Link
                to="/products"
                data-testid="hero-products-btn"
                className="border border-[#1D6B2A] text-[#1D6B2A] rounded-full px-8 py-4 font-semibold hover:bg-white transition-colors"
              >
                Browse Products
              </Link>
            </motion.div>

            <motion.div
              variants={fadeUp}
              custom={3}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
              className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl"
            >
              {STATS.map((s) => (
                <div key={s.l}>
                  <div className="font-head text-3xl font-semibold text-[#1D6B2A]">{s.n}</div>
                  <div className="text-xs text-[#3A4D3E] mt-1">{s.l}</div>
                </div>
              ))}
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <motion.div style={{ y: imgY }} className="relative rounded-[2rem] overflow-hidden aspect-[4/5] shadow-2xl shadow-[#1D6B2A]/15 bg-[#F5F1E8]">
                <img src={HERO_IMG} alt="Chennai Packs — custom branded disposable packaging range" className="w-full h-full object-cover" />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* TRUST BADGES */}
      <section className="py-14 border-b border-[#E2E8E4]">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid sm:grid-cols-3 gap-8">
          {[
            { icon: BadgeCheck, t: "Food-grade certified", d: "Every product safe for direct food contact." },
            { icon: Truck, t: "Reliable bulk supply", d: "On-time dispatch across Tamil Nadu & India." },
            { icon: Leaf, t: "Sustainable choices", d: "Full compostable & recyclable range." },
          ].map((b, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
              className="flex items-start gap-4"
            >
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#F8FAF8] text-[#1D6B2A] shrink-0">
                <b.icon size={24} />
              </span>
              <div>
                <h3 className="font-head font-semibold text-[#0A1C0E]">{b.t}</h3>
                <p className="text-sm text-[#3A4D3E] mt-0.5">{b.d}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-24" data-testid="home-categories">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
            <div>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1D6B2A] mb-3">Shop by category</p>
              <h2 className="font-head text-4xl md:text-5xl font-medium tracking-tight text-[#0A1C0E] max-w-xl">
                Every material, one supplier
              </h2>
            </div>
            <Link to="/products" className="text-[#1D6B2A] font-semibold flex items-center gap-1 hover:gap-2 transition-all">
              All products <ArrowRight size={18} />
            </Link>
          </div>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewport} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((c, i) => (
              <motion.div key={c.slug} variants={fadeUp} custom={i}>
                <Link
                  to={`/products?category=${c.slug}`}
                  data-testid={`home-category-${c.slug}`}
                  className="group block relative rounded-2xl overflow-hidden aspect-[16/11] border border-[#E2E8E4]"
                >
                  <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1C0E]/85 via-[#0A1C0E]/20 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <span className="text-[11px] font-bold uppercase tracking-widest text-[#FDCD00]">{c.material}</span>
                    <h3 className="font-head text-2xl font-semibold text-white mt-1 flex items-center gap-2">
                      {c.name}
                      <ArrowUpRight size={20} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    </h3>
                    <p className="text-white/70 text-sm mt-1 line-clamp-2">{c.description}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="py-24 bg-[#F8FAF8]" data-testid="home-featured">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
            <div>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1D6B2A] mb-3">Best sellers</p>
              <h2 className="font-head text-4xl md:text-5xl font-medium tracking-tight text-[#0A1C0E]">Featured products</h2>
            </div>
            <Link to="/products" className="text-[#1D6B2A] font-semibold flex items-center gap-1 hover:gap-2 transition-all">
              View all products <ArrowRight size={18} />
            </Link>
          </div>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewport} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
          </motion.div>
        </div>
      </section>

      {/* INDUSTRIES BENTO */}
      <section className="py-24" data-testid="home-industries">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
            <div>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1D6B2A] mb-3">Industries we serve</p>
              <h2 className="font-head text-4xl md:text-5xl font-medium tracking-tight text-[#0A1C0E] max-w-xl">
                Built for every corner of foodservice
              </h2>
            </div>
            <Link to="/industries" className="text-[#1D6B2A] font-semibold flex items-center gap-1 hover:gap-2 transition-all">
              All industries <ArrowRight size={18} />
            </Link>
          </div>
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="grid md:grid-cols-3 gap-5"
          >
            {industries.map((ind, i) => (
              <motion.div
                key={ind.name}
                variants={fadeUp}
                custom={i}
                className={`bg-white border border-[#E2E8E4] rounded-2xl p-7 hover:-translate-y-1 hover:shadow-xl transition-all duration-500 ${
                  i === 0 ? "md:col-span-2 md:row-span-1" : ""
                }`}
              >
                <span className="text-[#FDCD00] text-4xl font-head font-bold">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-head text-xl font-semibold text-[#0A1C0E] mt-3">{ind.name}</h3>
                <p className="text-[#3A4D3E] mt-2">{ind.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* WHY CHENNAI PACKS */}
      <section className="py-24 bg-[#F8FAF8]" data-testid="home-why">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="text-center mb-14">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1D6B2A] mb-3">Why Chennai Packs</p>
            <h2 className="font-head text-4xl md:text-5xl font-medium tracking-tight text-[#0A1C0E]">
              Six reasons brands stay with us
            </h2>
          </div>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewport} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY.map((w, i) => (
              <motion.div key={w.t} variants={fadeUp} custom={i} className="bg-white border border-[#E2E8E4] rounded-2xl p-7 hover:-translate-y-1 hover:shadow-xl transition-all duration-500">
                <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#F8FAF8] text-[#1D6B2A]">
                  <w.icon size={24} />
                </span>
                <h3 className="font-head text-xl font-semibold text-[#0A1C0E] mt-4">{w.t}</h3>
                <p className="text-[#3A4D3E] mt-2">{w.d}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CUSTOM BRANDING */}
      <section className="py-24" data-testid="home-branding">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 rounded-[2rem] overflow-hidden aspect-[4/3] shadow-xl order-2 lg:order-1"
          >
            <img src={BRANDING_IMG} alt="Custom branded packaging" className="w-full h-full object-cover" />
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} className="lg:col-span-6 order-1 lg:order-2">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1D6B2A] mb-3">Custom branding</p>
            <h2 className="font-head text-4xl md:text-5xl font-medium tracking-tight text-[#0A1C0E]">
              Your brand, on every box
            </h2>
            <p className="text-lg text-[#3A4D3E] mt-5 max-w-xl">
              Custom-printed cups, boxes and bags turn every order into free marketing and stronger
              brand recall — especially powerful for delivery-first cloud kitchens.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/custom-branding" data-testid="home-branding-cta" className="bg-[#1D6B2A] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#154d1e] transition-colors flex items-center gap-2">
                Explore custom branding <ArrowRight size={18} />
              </Link>
              <button onClick={() => openQuote("Custom Branding")} className="border border-[#1D6B2A] text-[#1D6B2A] rounded-full px-8 py-4 font-semibold hover:bg-[#F8FAF8] transition-colors">
                Get a quote
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* OUR PROCESS */}
      <section className="py-24 bg-[#0A1C0E] text-white relative overflow-hidden grain" data-testid="home-process">
        <div className="max-w-7xl mx-auto px-5 md:px-10 relative z-10">
          <div className="text-center mb-14">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FDCD00] mb-3">Our process</p>
            <h2 className="font-head text-4xl md:text-5xl font-medium tracking-tight text-white">
              From enquiry to your doorstep
            </h2>
          </div>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewport} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS.map((s, i) => (
              <motion.div key={s.t} variants={fadeUp} custom={i} className="relative bg-white/5 border border-white/10 rounded-2xl p-7">
                <span className="font-editorial text-4xl text-[#4CAF50]">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-head text-xl font-semibold text-white mt-3">{s.t}</h3>
                <p className="text-white/60 mt-2 text-sm">{s.d}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24" data-testid="home-testimonials">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="text-center mb-14">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1D6B2A] mb-3">Trusted by teams</p>
            <h2 className="font-head text-4xl md:text-5xl font-medium tracking-tight text-[#0A1C0E]">What our clients say</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((t, i) => (
              <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={viewport}
                className="bg-white border border-[#E2E8E4] rounded-2xl p-6 flex flex-col">
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} size={16} className="fill-[#FDCD00] text-[#FDCD00]" />
                  ))}
                </div>
                <p className="text-[#0A1C0E] leading-relaxed flex-1">&ldquo;{t.quote}&rdquo;</p>
                <div className="mt-5 pt-4 border-t border-[#E2E8E4]">
                  <div className="font-head font-semibold text-[#0A1C0E]">{t.name}</div>
                  <div className="text-xs text-[#3A4D3E]">{t.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 bg-[#F8FAF8]" data-testid="home-faqs">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1D6B2A] mb-3">FAQ</p>
            <h2 className="font-head text-4xl md:text-5xl font-medium tracking-tight text-[#0A1C0E]">Frequently asked questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4" data-testid="home-faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-white border border-[#E2E8E4] rounded-2xl px-6 data-[state=open]:shadow-md transition-shadow">
                <AccordionTrigger className="font-head text-lg font-semibold text-[#0A1C0E] hover:no-underline text-left py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#3A4D3E] text-base leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-8">
            <Link to="/faq" className="text-[#1D6B2A] font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all">
              See all FAQs <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* LATEST BLOGS */}
      <section className="py-24" data-testid="home-blogs">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
            <div>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1D6B2A] mb-3">From the journal</p>
              <h2 className="font-head text-4xl md:text-5xl font-medium tracking-tight text-[#0A1C0E]">Latest insights</h2>
            </div>
            <Link to="/blog" className="text-[#1D6B2A] font-semibold flex items-center gap-1 hover:gap-2 transition-all">
              All articles <ArrowRight size={18} />
            </Link>
          </div>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewport} className="grid md:grid-cols-3 gap-8">
            {blogs.map((p, i) => (
              <motion.article key={p.slug} variants={fadeUp} custom={i} className="group">
                <Link to={`/blog/${p.slug}`} data-testid={`home-blog-${p.slug}`} className="block">
                  <div className="rounded-2xl overflow-hidden aspect-[16/10] border border-[#E2E8E4] mb-5">
                    <img src={p.cover_image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <span className="text-xs font-bold tracking-[0.15em] uppercase text-[#1D6B2A]">{p.category}</span>
                  <h3 className="font-head text-xl font-semibold text-[#0A1C0E] mt-2 group-hover:text-[#1D6B2A] transition-colors">{p.title}</h3>
                  <p className="text-[#3A4D3E] mt-2 line-clamp-2">{p.excerpt}</p>
                </Link>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section className="py-24 bg-[#F8FAF8]" data-testid="home-contact">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-start">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} className="lg:col-span-5">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1D6B2A] mb-3">Get in touch</p>
            <h2 className="font-head text-4xl md:text-5xl font-medium tracking-tight text-[#0A1C0E]">
              Let&apos;s package your brand better
            </h2>
            <p className="text-lg text-[#3A4D3E] mt-5">
              Share your requirement and our team will send tailored bulk pricing — fast, with no obligation.
            </p>
            <div className="mt-8 space-y-3 text-[#0A1C0E]">
              <a href="tel:917339252701" className="flex items-center gap-3 font-semibold hover:text-[#1D6B2A]">
                <span className="grid place-items-center w-10 h-10 rounded-xl bg-white border border-[#E2E8E4] text-[#1D6B2A]"><Truck size={18} /></span>
                +91 7339252701
              </a>
              <a href="mailto:sales@chennaipacks.com" className="flex items-center gap-3 font-semibold hover:text-[#1D6B2A]">
                <span className="grid place-items-center w-10 h-10 rounded-xl bg-white border border-[#E2E8E4] text-[#1D6B2A]"><BadgeCheck size={18} /></span>
                sales@chennaipacks.com
              </a>
            </div>
          </motion.div>
          <div className="lg:col-span-7 bg-white border border-[#E2E8E4] rounded-3xl p-6 md:p-8 shadow-sm">
            <QuoteForm />
          </div>
        </div>
      </section>
    </div>
  );
}
